import React from 'react';
import { LocationWorldMap } from '@iterable/deprecated_react_components';
import {
  convertLatLonToPosition,
  convertTopPositionToLat,
  convertLeftPositionToLon,
} from 'utils/coordinates';
import { getElementSizing } from 'utils/element';
import { parseToNumber } from 'utils/helpers';
import noop from 'utils/noop';
import CityDropdown from './CityDropdown';
import { getCityByLatLon, determineDirection, withinRange, formatCityLabel } from './helpers';
import {
  StyledCoreNumberInput,
  Label,
  CityLabel,
  Trigger,
  CityDropdownContainer,
  StyledSemanticPopup,
  ReadOnlyContainer,
} from './LocationPicker.styled';

// type Props = {
//   /** Additional classes. */
//   className: string,
//   /** Additional classes for lat input. */
//   classNameLatInput: string,
//   /** Additional classes for lon input. */
//   classNameLonInput: string,
//   /** Additional classes for read only mode. */
//   classNameReadOnly: string,
//   /** If distance picker is disabled. */
//   disabled?: boolean,
//   /** Displays the input fields inline. */
//   inline: boolean,
//   /** Width of map in pixels */
//   mapWidth: number,
//   /** The latitude of the location. */
//   lat: number,
//   /**
//    * The latitude limit. Do not change only for testing purposes.
//    * @private
//    */
//   latLimit: number,
//   /** The longitude of the location. */
//   lon: number,
//   /**
//    * The longitude limit. Do not change only for testing purposes.
//    * @private
//    */
//   lonLimit: number,
//   /**
//    * Callback function when lat or lon value is changed.
//    *
//    * ```
//    * onChange(e, valueProps) => {}
//    * ```
//    * @param { Object } e: The change event.
//    * @param { Object } valueProps: Object containing the changed values.
//    * @return { void }
//    */
//   onChange: (e: {}, props: {}) => {},
//   /** Hides the popup and displays the map inline. */
//   hidePopup: boolean,
//   /** If location picker is in queryBuilder, enable this flag for view mode. */
//   inQueryBuilder: boolean,
// };

export default class LocationPicker extends React.Component {
  static defaultProps = {
    disabled: false,
    hidePopup: false,
    inline: false,
    inQueryBuilder: false,
    lat: 0,
    latLimit: 90,
    lon: 0,
    lonLimit: 180,
    onChange: noop,
  };

  constructor(props) {
    super(props);

    this.state = {
      open: false,
    };
  }

  /**
   * withinRange
   * If a given number is within the positve and negative limit.
   * @returns { Boolean } If the given value is within range.
   */
  withinRange = withinRange;

  /**
   * setCity
   * Gets a matching city of the current lat / lon value.
   * @returns { void }
   */
  setCity = () => {
    const { lat, lon } = this.props;
    const city = getCityByLatLon(lat, lon);
    this.setState({ city });
  };

  /**
   * determineDirection
   * @returns { String } The direction of the value.
   */
  determineDirection = determineDirection;

  /**
   * handleChangeLat
   * Callback function when lat value changes.
   * @param { Object } e: The change event.
   * @param { Object } updatedProps: Updated props containing updated lat value.
   * @returns { void }
   */
  handleChangeLat = (e, { value }) => {
    const { latLimit, onChange } = this.props;
    if (this.withinRange(value, latLimit)) {
      onChange(e, { ...this.props, lat: parseToNumber(value) });
    }
  };

  /**
   * handleChangeLon
   * Callback function when lon value changes.
   * @param { Object } e: The change event.
   * @param { Object } updatedProps: Updated props containing updated lon value.
   * @returns { void }
   */
  handleChangeLon = (e, { value }) => {
    const { lonLimit, onChange } = this.props;
    if (this.withinRange(value, lonLimit)) {
      onChange(e, { ...this.props, lon: value });
    }
  };

  /**
   * handleChangeCity
   * Callback function when lat/lon value changes from selecting a city.
   * @param { Object } e: The change event.
   * @param { Object } updatedProps: Updated props containing the city lat/lon values.
   * @returns { void }
   */
  handleChangeCity = (e, { lat, lon }) => {
    this.props.onChange(e, { lat, lon });
  };

  /**
   * handleClick
   * Callback function when map is clicked. Calculates lat/lon of click position.
   * @param { Object } e: The click event.
   * @returns { void }
   */
  handleClick = (e) => {
    const { clientX, clientY, target } = e;
    const { left: x, top: y, height, width } = getElementSizing(target);
    const top = clientY - y;
    const topDecimal = top / height;
    const lat = convertTopPositionToLat(topDecimal * 100);

    const left = clientX - x;
    const leftDecimal = left / width;
    const lon = convertLeftPositionToLon(leftDecimal * 100);
    this.props.onChange(e, { lat, lon });
    this.setState({ city: {} });
  };

  /**
   * handleClose
   * Callback function when popup closes.
   * @param { Object } e: The close event.
   * @returns { void }
   */
  handleClose = (e) => {
    if (e.fromElement === null) {
      this.setState({ open: false, city: {} });
    }
  };

  /**
   * handleOpen
   * Callback function when popup opens.
   * @returns { void }
   */
  handleOpen = () => {
    this.setCity();
    this.setState({ open: true });
  };

  /**
   * renderInput
   * Creates an number input box.
   * @param { Number } value: The value of the input.
   * @param { React.ReactNode } icon: The icon/label of the input.
   * @param { String } className: Additional classes for the input.
   * @param { Boolean } append: If the input is appended.
   * @param { Function } onChange: Callback function when value changes.
   * @returns { React.ReactNode } The number input.
   */
  renderInput = (value, icon, className, append, onChange, disabled) => (
    <StyledCoreNumberInput
      allowDecimal
      append={append}
      className={className}
      fluid
      icon={icon}
      inline={this.props.inline}
      iconPosition="left"
      onChange={onChange}
      prepend={!append}
      value={value}
      disabled={disabled}
      disableIncrement={disabled}
      disableDecrement={disabled}
    />
  );

  /**
   * formatCityLabel
   * Formats the city label for the view mode popup.
   */
  formatCityLabel = formatCityLabel;

  /**
   * renderLatInput
   * Creates the lat input box.
   * @returns { React.Component } The lat input input.
   */
  renderLatInput = () => {
    const { classNameLatInput, lat, disabled } = this.props;
    const direction = this.determineDirection('(N)', '(S)', lat);
    const label = `Lat ${direction}`;

    const latLabel = <Label>{label}</Label>;
    return this.renderInput(
      lat,
      latLabel,
      classNameLatInput,
      false,
      this.handleChangeLat,
      disabled,
    );
  };

  /**
   * renderLonInput
   * Creates the lon input box.
   * @returns { React.Component } The lon input input.
   */
  renderLonInput = () => {
    const { classNameLonInput, lon, disabled } = this.props;
    const direction = this.determineDirection('(E)', '(W)', lon);
    const label = `Lon ${direction}`;

    const lonLabel = <Label>{label}</Label>;
    return this.renderInput(lon, lonLabel, classNameLonInput, true, this.handleChangeLon, disabled);
  };

  render() {
    const { className, classNameReadOnly, inline, hidePopup, inQueryBuilder, lat, lon, disabled } =
      this.props;
    const { city, open } = this.state;
    const location = convertLatLonToPosition(lat, lon);
    const worldMap = <LocationWorldMap onClick={this.handleClick} {...location} />;
    const popupContent = (
      <div>
        {worldMap}
        <CityDropdownContainer>
          <CityDropdown city={city} lat={lat} lon={lon} onChange={this.handleChangeCity} />
        </CityDropdownContainer>
      </div>
    );

    const trigger = (
      <Trigger $isInline={inline || inQueryBuilder} className={className}>
        {hidePopup && worldMap}
        <div className="cssLocationInput" data-test="collections-location-picker">
          {this.renderLatInput()}
          {this.renderLonInput()}
        </div>
      </Trigger>
    );

    const popup = (
      <StyledSemanticPopup
        trigger={trigger}
        content={popupContent}
        on="click"
        onClose={this.handleClose}
        onOpen={this.handleOpen}
        open={open}
        position="top left"
        disabled={disabled}
      />
    );

    let label;
    if (inQueryBuilder) {
      const worldMapReadOnly = (
        <div>
          <LocationWorldMap mapWidth={300} {...location} />
          {city && <CityLabel>{this.formatCityLabel(city)}</CityLabel>}
        </div>
      );
      const labelString = `${lat}, ${lon}`;
      const labelContent = (
        <ReadOnlyContainer className={classNameReadOnly} key="value">
          {labelString}
        </ReadOnlyContainer>
      );
      label = (
        <StyledSemanticPopup
          trigger={labelContent}
          content={worldMapReadOnly}
          on="hover"
          onOpen={this.setCity}
          position="top center"
          disabled={disabled}
        />
      );
    }

    return (
      <>
        {label}
        {hidePopup ? trigger : popup}
      </>
    );
  }
}
